
import { DiaryEntry } from "@/utils/diary";
import EntryCard from "@/components/EntryCard";
import { memo, useMemo, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";
import { Skeleton } from "@/components/ui/skeleton";

interface EntryCardsListProps {
  entries: DiaryEntry[];
  onDelete: (id: string) => void;
  onUpdate: (updatedEntry: DiaryEntry) => void;
  isLoading?: boolean;
}

const ITEMS_PER_PAGE = 5;

const EntryCardsList = memo(({ entries, onDelete, onUpdate, isLoading = false }: EntryCardsListProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { language } = useLanguage();
  
  // Reset to page 1 when entries change
  useMemo(() => {
    setCurrentPage(1);
  }, [entries.length]); // Only reset when entries count changes
  
  // Early render for skeleton loaders
  if (isLoading) {
    return (
      <div className="space-y-6">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={`skeleton-${index}`} className="border rounded-lg p-4">
            <div className="space-y-4">
              <div className="flex justify-between">
                <Skeleton className="h-6 w-1/3" />
                <Skeleton className="h-6 w-1/4" />
              </div>
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  
  // Return null if no entries
  if (!entries.length) {
    return null;
  }
  
  // Create memoized key generator to avoid recreating on each render
  const getEntryKey = useMemo(() => (entry: DiaryEntry) => entry.id, []);
  
  // Calculate total pages and current page entries
  const totalPages = Math.ceil(entries.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedEntries = entries.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  
  // Memoize page navigation handlers to prevent recreation on each render
  const handlePreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage]);
  
  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage, totalPages]);
  
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6">
        {paginatedEntries.map((entry) => (
          <div 
            key={getEntryKey(entry)}
            className="transform transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
          >
            <EntryCard
              entry={entry}
              onDelete={() => onDelete(entry.id)}
              onUpdate={onUpdate}
            />
          </div>
        ))}
      </div>
      
      {totalPages > 1 && (
        <div className="flex items-center justify-between py-4 mt-4">
          <div className="text-sm text-muted-foreground">
            {`${t('entries.showingPage', language)} ${currentPage} / ${totalPages}`}
          </div>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              {t('entries.previous', language)}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              {t('entries.next', language)}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});

EntryCardsList.displayName = "EntryCardsList";

export default EntryCardsList;
